import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import bg4 from "../assets/bg4.jpg";
import CartIcon from "./Carticon";

const Home = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Enhanced scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
      setShowBackToTop(window.scrollY > 300);
      
      // Parallax effect for hero section
      const scrolled = window.pageYOffset;
      const parallax = document.getElementById('hero');
      if (parallax) {
        parallax.style.transform = `translateY(${scrolled * 0.5}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Enhanced click outside handler with escape key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest(".nav-container")) {
        setIsMenuOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isMenuOpen]);

  const navigate = useNavigate();

  // Enhanced cart functions with local storage sync
  const handleUpdateCart = (menuId, quantity) => {
    setCart((prevCart) => {
      const updatedCart = quantity <= 0
        ? prevCart.filter((item) => item.menu_id !== menuId)
        : prevCart.map((item) =>
            item.menu_id === menuId ? { ...item, quantity } : item
          );
      
      // Sync with localStorage
      localStorage.setItem("currentCart", JSON.stringify(updatedCart));
      localStorage.setItem("cartTimestamp", Date.now().toString());
      
      return updatedCart;
    });
  };

  const handleAddToCart = (item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((i) => i.menu_id === item.menu_id);
      const updatedCart = existingItem
        ? prevCart.map((i) =>
            i.menu_id === item.menu_id ? { ...i, quantity: i.quantity + 1 } : i
          )
        : [...prevCart, { ...item, quantity: 1 }];
      
      localStorage.setItem("currentCart", JSON.stringify(updatedCart));
      return updatedCart;
    });
  };

  // Enhanced menu handlers
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Enhanced scroll with offset for fixed header
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth"
      });
    }
    closeMenu();
  };

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getCurrentDay = () => {
    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return days[new Date().getDay()];
  };

  // Enhanced restaurant search with debouncing
  const handleSearchChange = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.length > 2) {
      setLoadingRestaurants(true);
      try {
        // Simulate slight delay for better UX
        await new Promise(resolve => setTimeout(resolve, 300));
        const response = await axios.get(
          `${config.API_BASE_URL}/restaurant/search-restaurants?query=${query}`
        );
        setSearchResults(response.data);
      } catch (error) {
        console.error("Error fetching restaurants:", error);
        setSearchResults([]);
      } finally {
        setLoadingRestaurants(false);
      }
    } else {
      setSearchResults([]);
      setLoadingRestaurants(false);
    }
  };

  // Enhanced cart state with recovery
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("currentCart");
    const cartTimestamp = localStorage.getItem("cartTimestamp");
    const twentyFourHours = 24 * 60 * 60 * 1000;
    
    if (savedCart && cartTimestamp) {
      const timeDiff = Date.now() - parseInt(cartTimestamp);
      if (timeDiff < twentyFourHours) {
        return JSON.parse(savedCart);
      } else {
        localStorage.removeItem("currentCart");
        localStorage.removeItem("cartTimestamp");
      }
    }
    return [];
  });

  const [showCartModal, setShowCartModal] = useState(false);
  const [touching, setTouching] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [cartPosition, setCartPosition] = useState(() => {
    const saved = localStorage.getItem("cartPosition");
    return saved ? JSON.parse(saved) : { x: window.innerWidth - 100, y: 20 };
  });
  const [touchPosition, setTouchPosition] = useState({
    x: window.innerWidth - 80,
    y: window.innerHeight / 2,
  });

  // Enhanced drag and drop with boundaries
  const handleDragStart = (e) => {
    const rect = e.target.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    e.dataTransfer.setData("text/plain", `${offsetX},${offsetY}`);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrag = (e) => {
    if (e.clientX && e.clientY) {
      const [offsetX, offsetY] = e.dataTransfer
        .getData("text/plain")
        .split(",")
        .map(Number);
      
      const x = Math.min(
        Math.max(20, e.clientX - offsetX),
        window.innerWidth - 120
      );
      const y = Math.min(
        Math.max(20, e.clientY - offsetY),
        window.innerHeight - 120
      );
      
      setCartPosition({ x, y });
      localStorage.setItem("cartPosition", JSON.stringify({ x, y }));
    }
  };

  const handleMobileTouchStart = (e) => {
    const touch = e.touches[0];
    setTouching(true);
    setDragOffset({
      x: touch.clientX - touchPosition.x,
      y: touch.clientY - touchPosition.y,
    });
  };

  const handleMobileTouchMove = (e) => {
    if (touching && e.touches[0]) {
      const touch = e.touches[0];
      const newX = Math.min(
        Math.max(20, touch.clientX - dragOffset.x),
        window.innerWidth - 100
      );
      const newY = Math.min(
        Math.max(20, touch.clientY - dragOffset.y),
        window.innerHeight - 100
      );
      setTouchPosition({ x: newX, y: newY });
    }
  };

  const handleMobileTouchEnd = () => {
    setTouching(false);
    localStorage.setItem("cartTouchPosition", JSON.stringify(touchPosition));
  };

  const calculateTotal = () =>
    cart
      .reduce((total, item) => total + (item.price || 0) * (item.quantity || 0), 0)
      .toFixed(2);

  const handleCheckout = () => {
    navigate("/cart", {
      state: { 
        cart, 
        restaurant_id: localStorage.getItem("restaurant_id") 
      },
    });
  };

  // Enhanced restaurant card click handler
  const handleRestaurantSelect = (restaurant) => {
    setCart([]);
    localStorage.removeItem("currentCart");
    localStorage.removeItem("cartTimestamp");
    localStorage.setItem("previous_restaurant_id", restaurant.restaurant_id);
  };

  // Auto-close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-poppins">
      {/* Enhanced Header with Glass Morphism */}
      <header
        className={`${
          isScrolled 
            ? "fixed top-0 shadow-2xl backdrop-blur-lg bg-[#4e7ab4]/95" 
            : "relative bg-[#4e7ab4]"
        } w-full z-50 text-white transition-all duration-500 ease-in-out`}
      >
        <div className="flex justify-between items-center max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
          <div className="nav-container relative">
            {/* Enhanced Hamburger Menu */}
            <button
              className={`hamburger cursor-pointer md:hidden p-3 rounded-xl transition-all duration-300 ${
                isMenuOpen ? 'bg-[#3a5f8a] rotate-90' : 'hover:bg-[#3a5f8a]'
              }`}
              onClick={toggleMenu}
              aria-label="Toggle Menu"
              aria-expanded={isMenuOpen}
            >
              <div className={`w-6 h-0.5 bg-white mb-1.5 transition-all duration-300 ${
                isMenuOpen ? 'rotate-45 translate-y-2' : ''
              }`}></div>
              <div className={`w-6 h-0.5 bg-white mb-1.5 transition-all duration-300 ${
                isMenuOpen ? 'opacity-0' : ''
              }`}></div>
              <div className={`w-6 h-0.5 bg-white transition-all duration-300 ${
                isMenuOpen ? '-rotate-45 -translate-y-2' : ''
              }`}></div>
            </button>
            
            {/* Enhanced Navigation */}
            <nav
              className={`${
                isMenuOpen 
                  ? "block animate-slideInDown" 
                  : "hidden"
              } md:flex md:items-center md:justify-center absolute top-full left-0 bg-[#4e7ab4] md:bg-transparent p-6 rounded-2xl shadow-2xl md:shadow-none md:relative md:p-0 w-64 md:w-auto text-center border border-[#3a5f8a] md:border-none backdrop-blur-lg md:backdrop-blur-none`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:space-x-6 lg:space-x-8 space-y-3 md:space-y-0 w-full font-semibold text-lg">
                <Link
                  to="/login"
                  className="hover:text-[#abbfff] w-full md:w-auto py-3 md:py-2 transition-all duration-300 border-b border-[#3a5f8a] md:border-none hover:scale-105"
                  onClick={closeMenu}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="hover:text-[#abbfff] w-full md:w-auto py-3 md:py-2 transition-all duration-300 border-b border-[#3a5f8a] md:border-none hover:scale-105"
                  onClick={closeMenu}
                >
                  Sign Up
                </Link>
                <button
                  className="hover:text-[#abbfff] w-full md:w-auto text-center py-3 md:py-2 transition-all duration-300 border-b border-[#3a5f8a] md:border-none hover:scale-105"
                  onClick={() => scrollToSection("features")}
                >
                  Features
                </button>
                <button
                  className="hover:text-[#abbfff] w-full md:w-auto text-center py-3 md:py-2 transition-all duration-300 border-b border-[#3a5f8a] md:border-none hover:scale-105"
                  onClick={() => scrollToSection("pricing")}
                >
                  Pricing
                </button>
                <button
                  className="hover:text-[#abbfff] w-full md:w-auto text-center py-3 md:py-2 transition-all duration-300 border-b border-[#3a5f8a] md:border-none hover:scale-105"
                  onClick={() => scrollToSection("testimonials")}
                >
                  Testimonials
                </button>
                <Link
                  to="/contact"
                  className="hover:text-[#abbfff] w-full md:w-auto py-3 md:py-2 transition-all duration-300 hover:scale-105"
                  onClick={closeMenu}
                >
                  Contact
                </Link>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Enhanced Main Content */}
      <main className={`flex-1 flex flex-col items-center ${isScrolled ? 'pt-20' : ''}`}>
        
        {/* Enhanced Hero Section with Parallax */}
        <section
          id="hero"
          className="relative w-full min-h-[85vh] bg-cover bg-fixed bg-center bg-no-repeat flex flex-col justify-center items-center text-center px-4 overflow-hidden"
          style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${bg4})` }}
        >
          <div className="relative z-10 max-w-6xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight mb-6 leading-tight">
              Restaurant Table-Server-Assist
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl text-white mb-8 leading-relaxed max-w-4xl mx-auto font-light">
              Revolutionize your restaurant management with our all-in-one digital solution
            </p>
            <div>
              <Link
                to="/register"
                className="inline-block px-12 py-5 bg-gradient-to-r from-[#abbfff] to-[#7db1e5] text-gray-900 font-bold rounded-2xl hover:from-[#4e7ab4] hover:to-[#abbfff] hover:text-white transition-all duration-500 transform hover:scale-110 shadow-2xl text-lg lg:text-xl hover:shadow-3xl"
              >
                🚀 Let's Start
              </Link>
            </div>
          </div>
          
          {/* Animated scroll indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
            </div>
          </div>
        </section>

        {/* Enhanced Restaurant Search */}
        <section id="restaurant-search" className="w-full max-w-6xl px-4 py-16 md:py-24 -mt-10 relative z-20">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 border border-white/20">
            <h2 className="text-4xl md:text-5xl font-black text-gray-800 text-center mb-12 bg-gradient-to-r from-[#4e7ab4] to-[#7db1e5] bg-clip-text text-transparent">
              Discover Amazing Restaurants
            </h2>
            
            <div className="relative">
              <input
                type="text"
                placeholder="🔍 Search for restaurants by name, city, or zipcode..."
                onChange={handleSearchChange}
                value={searchQuery}
                className="w-full p-5 text-lg border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-[#4e7ab4] focus:ring-4 focus:ring-[#4e7ab4]/20 transition-all duration-300 shadow-lg"
              />
              
              {loadingRestaurants && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#4e7ab4]"></div>
                </div>
              )}
            </div>

            {/* Enhanced Search Results */}
            <div className="mt-8 space-y-6 max-h-96 overflow-y-auto pr-2">
              {searchResults.map((restaurant, index) => (
                <div
                  key={restaurant.id}
                  className="bg-gradient-to-r from-gray-50 to-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] group"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <h3 className="text-2xl font-bold text-gray-800 group-hover:text-[#4e7ab4] transition-colors duration-300">
                          {restaurant.name}
                        </h3>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 ml-4">
                          ⭐ {restaurant.rating || '4.5'}
                        </span>
                      </div>
                      
                      <p className="text-gray-600 text-lg mt-2 leading-relaxed">
                        {restaurant.description}
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                        <p className="flex items-center text-gray-700">
                          <span className="mr-3 text-xl">📞</span>
                          <span className="font-medium">{restaurant.phone}</span>
                        </p>
                        <p className="flex items-center text-gray-700">
                          <span className="mr-3 text-xl">📍</span>
                          <span className="font-medium">{restaurant.address}, {restaurant.city}</span>
                        </p>
                      </div>

                      {/* Enhanced Opening Hours */}
                      <details className="mt-4 group/details">
                        <summary className="cursor-pointer text-lg text-gray-600 hover:text-gray-800 transition-colors duration-200 font-medium list-none">
                          <span className="flex items-center">
                            🕒 Today's Hours: {restaurant.opening_hours[getCurrentDay()]?.open || 'N/A'} - {restaurant.opening_hours[getCurrentDay()]?.close || 'N/A'}
                            <span className="ml-2 transform group-open/details:rotate-180 transition-transform duration-300">▼</span>
                          </span>
                        </summary>
                        <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200 shadow-inner">
                          <h4 className="font-bold text-gray-800 mb-3">Weekly Schedule:</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {Object.entries(restaurant.opening_hours).map(([day, hours]) => (
                              <div key={day} className={`flex justify-between items-center py-2 px-3 rounded-lg ${
                                day === getCurrentDay() ? 'bg-[#4e7ab4]/10 border border-[#4e7ab4]/20' : 'bg-gray-50'
                              }`}>
                                <span className={`font-medium capitalize ${day === getCurrentDay() ? 'text-[#4e7ab4]' : 'text-gray-700'}`}>
                                  {day}
                                </span>
                                <span className="text-gray-600">
                                  {hours.open} - {hours.close}
                                  {hours.pickup && " 🏃‍♂️"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </details>
                    </div>
                    
                    <div className="lg:text-right">
                      <Link
                        to={`/menu-display?token=${restaurant.token}`}
                        onClick={() => handleRestaurantSelect(restaurant)}
                      >
                        <button className="bg-gradient-to-r from-[#4e7ab4] to-[#7db1e5] text-white px-8 py-4 rounded-xl hover:from-[#3a5f8a] hover:to-[#4e7ab4] transition-all duration-300 transform hover:scale-105 font-bold text-lg shadow-lg hover:shadow-xl w-full lg:w-auto">
                          🍽️ Order Now
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
              
              {searchQuery.length > 2 && searchResults.length === 0 && !loadingRestaurants && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-2xl font-bold text-gray-600 mb-2">No restaurants found</h3>
                  <p className="text-gray-500">Try searching with different keywords</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Enhanced Features Section */}
        <section id="features" className="w-full max-w-7xl px-4 py-16 md:py-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-800 mb-6 bg-gradient-to-r from-[#4e7ab4] to-[#7db1e5] bg-clip-text text-transparent">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to streamline your restaurant operations and delight your customers
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "📦",
                title: "Order Management",
                features: [
                  "🕒 Real-time order tracking",
                  "📲 Instant notifications",
                  "📊 Order history and analytics",
                  "🔔 Custom order status updates"
                ],
                gradient: "from-blue-500 to-cyan-500"
              },
              {
                icon: "📄",
                title: "Menu Management",
                features: [
                  "🖼️ Digital menu creation",
                  "⚙️ Easy menu updates",
                  "📑 Category organization",
                  "🎉 Special offers management"
                ],
                gradient: "from-green-500 to-emerald-500"
              },
              {
                icon: "💬",
                title: "Customer Experience",
                features: [
                  "🏆 Loyalty rewards",
                  "👥 Customizable profiles",
                  "📥 Feedback collection",
                  "📧 Email notifications"
                ],
                gradient: "from-purple-500 to-pink-500"
              },
              {
                icon: "📱",
                title: "Mobile Ready",
                features: [
                  "📲 Responsive design",
                  "📱 Touch optimized",
                  "⚡ Fast loading",
                  "📶 Offline support"
                ],
                gradient: "from-orange-500 to-red-500"
              },
              {
                icon: "🔒",
                title: "Security & Privacy",
                features: [
                  "🔐 Secure payments",
                  "📊 Data encryption",
                  "🛡️ Privacy protection",
                  "✅ GDPR compliant"
                ],
                gradient: "from-indigo-500 to-blue-500"
              },
              {
                icon: "📈",
                title: "Analytics & Reports",
                features: [
                  "📊 Sales analytics",
                  "📈 Performance reports",
                  "🎯 Customer insights",
                  "📋 Inventory tracking"
                ],
                gradient: "from-teal-500 to-green-500"
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-transparent group hover:scale-105"
              >
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center text-3xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-[#4e7ab4] group-hover:to-[#7db1e5] group-hover:bg-clip-text">
                  {feature.title}
                </h3>
                <ul className="space-y-3">
                  {feature.features.map((item, idx) => (
                    <li key={idx} className="text-gray-600 text-lg flex items-start group-hover:text-gray-700 transition-colors duration-300">
                      <span className="mr-3 mt-1 text-xl">{item.split(' ')[0]}</span>
                      <span className="leading-relaxed">{item.split(' ').slice(1).join(' ')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Enhanced Pricing Section */}
        <section id="pricing" className="w-full max-w-6xl px-4 py-16 md:py-24 bg-gradient-to-br from-white to-gray-50 rounded-3xl mx-4 my-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-800 mb-6 bg-gradient-to-r from-[#4e7ab4] to-[#7db1e5] bg-clip-text text-transparent">
              Choose Your Plan
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Flexible pricing designed to grow with your restaurant business
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Starter",
                price: "$50",
                description: "Perfect for small restaurants",
                features: [
                  "✅ Essential features",
                  "✅ Real-time ordering",
                  "✅ Basic analytics",
                  "✅ Email support",
                  "✅ 1 location",
                  "✅ 100 orders/month"
                ],
                popular: false
              },
              {
                name: "Professional",
                price: "$100",
                description: "Ideal for growing businesses",
                features: [
                  "✅ All starter features",
                  "✅ Advanced analytics",
                  "✅ 24/7 support",
                  "✅ Customizable menus",
                  "✅ 3 locations",
                  "✅ 500 orders/month",
                  "✅ Marketing tools"
                ],
                popular: true
              },
              {
                name: "Enterprise",
                price: "$200",
                description: "For large restaurant chains",
                features: [
                  "✅ All Professional features",
                  "✅ Priority support",
                  "✅ Custom integrations",
                  "✅ Dedicated account manager",
                  "✅ Unlimited locations",
                  "✅ Unlimited orders",
                  "✅ API access",
                  "✅ White-label solution"
                ],
                popular: false
              }
            ].map((plan, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-3xl shadow-xl border-2 transition-all duration-500 hover:scale-105 ${
                  plan.popular 
                    ? 'border-[#4e7ab4] relative shadow-2xl' 
                    : 'border-gray-100 hover:border-[#4e7ab4]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-[#4e7ab4] to-[#7db1e5] text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <h3 className="text-3xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-6">{plan.description}</p>
                <div className="text-4xl font-black text-gray-900 my-6">
                  {plan.price}<span className="text-lg font-normal text-gray-600">/month</span>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="text-gray-700 text-lg flex items-center">
                      <span className="mr-3 text-green-500 text-xl">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 block text-center ${
                    plan.popular
                      ? 'bg-gradient-to-r from-[#4e7ab4] to-[#7db1e5] text-white hover:shadow-2xl hover:scale-105'
                      : 'bg-gray-100 text-gray-800 hover:bg-[#4e7ab4] hover:text-white'
                  } shadow-lg`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Enhanced Testimonials Section */}
        <section id="testimonials" className="w-full max-w-6xl px-4 py-16 md:py-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-800 mb-6 bg-gradient-to-r from-[#4e7ab4] to-[#7db1e5] bg-clip-text text-transparent">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join thousands of satisfied restaurant owners who transformed their business
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "John Doe",
                role: "Restaurant Owner",
                avatar: "https://avataaars.io/?avatarStyle=Circle&topType=ShortHairShortFlat&accessoriesType=Blank&hairColor=BrownDark&facialHairType=Blank&clotheType=BlazerShirt&eyeType=Default&eyebrowType=Default&mouthType=Smile&skinColor=Light",
                quote: "This system has completely transformed how we manage orders and interact with customers. Highly recommended!",
                rating: 5
              },
              {
                name: "Jane Smith",
                role: "Restaurant Manager",
                avatar: "https://avataaars.io/?avatarStyle=Circle&topType=LongHairStraight&accessoriesType=Blank&hairColor=Black&facialHairType=Blank&clotheType=BlazerSweater&eyeType=Default&eyebrowType=Default&mouthType=Smile&skinColor=Light",
                quote: "A game changer for our restaurant! The streamlined process makes everything faster and more efficient.",
                rating: 5
              },
              {
                name: "Mark Johnson",
                role: "Operations Manager",
                avatar: "https://avataaars.io/?avatarStyle=Circle&topType=ShortHairDreads01&accessoriesType=Round&hairColor=Black&facialHairType=BeardMedium&facialHairColor=Black&clotheType=Hoodie&clotheColor=Gray&eyeType=Happy&eyebrowType=Default&mouthType=Smile&skinColor=Light",
                quote: "The user interface is so intuitive, and the system works flawlessly. It has significantly reduced the stress of managing orders!",
                rating: 5
              }
            ].map((testimonial, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-[#4e7ab4] to-[#7db1e5] p-8 rounded-3xl shadow-2xl text-white hover:shadow-3xl transition-all duration-500 hover:scale-105"
              >
                <div className="flex justify-center mb-6">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                </div>
                <div className="flex justify-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-300 text-xl">⭐</span>
                  ))}
                </div>
                <p className="text-lg italic mb-6 leading-relaxed text-center">
                  "{testimonial.quote}"
                </p>
                <div className="text-center">
                  <p className="font-bold text-xl">{testimonial.name}</p>
                  <p className="text-blue-100">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {showBackToTop && (
          <button
            onClick={handleBackToTop}
            className="fixed bottom-8 right-8 bg-gradient-to-r from-[#4e7ab4] to-[#7db1e5] text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 z-50 flex items-center justify-center group hover:scale-110 backdrop-blur-sm"
            aria-label="Back to top"
          >
            <svg
              className="w-6 h-6 group-hover:-translate-y-1 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
          </button>
        )}
      </main>

      {cart.length > 0 && (
        <CartIcon
          cart={cart}
          showCartModal={showCartModal}
          setShowCartModal={setShowCartModal}
          touching={touching}
          touchPosition={touchPosition}
          handleMobileTouchStart={handleMobileTouchStart}
          handleMobileTouchMove={handleMobileTouchMove}
          handleMobileTouchEnd={handleMobileTouchEnd}
          cartPosition={cartPosition}
          handleDragStart={handleDragStart}
          handleDrag={handleDrag}
          handleUpdateCart={handleUpdateCart}
          handleAddToCart={handleAddToCart}
          calculateTotal={calculateTotal}
          handleCheckout={handleCheckout}
        />
      )}
    </div>
  );
};

export default Home;